/**
 * Theme RichTextContent — same API as `@vibesites/runtime/react`, but rewrites
 * `property_bedrooms_range` field tokens to a discrete bedroom list
 * (e.g. "2-2" → "2", "studio-3" → "Studio, 1, 2 & 3") before resolve.
 */
import React, { useMemo } from 'react';
import {
  resolveRichText,
  stripEdgeEmptyParagraphs,
  verifyCrossLinks,
} from '@vibesites/runtime';
import {
  EditableToken,
  useContentToken,
  useVibesites,
} from '@vibesites/runtime/react';
import { usePropertyWithBedroomOptions } from '../../hooks/usePropertyWithBedroomOptions';

const PHRASING_ONLY_TAGS = new Set([
  'p', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'a', 'label',
]);

const BLANK_P_RE = /<p\b[^>]*>(?:\s|<br\s*\/?>)*<\/p>/gi;
const BLOCK_CONTENT_RE = /<(?:p|div|h[1-6]|ul|ol|li|blockquote)[\s>]/i;

function stripWrappingParagraph(html: string, tag: string): string {
  if (!PHRASING_ONLY_TAGS.has(tag)) return html;
  const cleaned = stripEdgeEmptyParagraphs(html);
  const match = cleaned.match(/^<p>([\s\S]*)<\/p>$/);
  if (!match) return cleaned || html;
  const inner = match[1];
  if (/<p[\s>]|<div[\s>]|<h[1-6][\s>]/i.test(inner)) return cleaned;
  return inner;
}

function collapseBlankParagraphs(html: string): string {
  return html.replace(BLANK_P_RE, '');
}

function hasBlockContent(html: string): boolean {
  return BLOCK_CONTENT_RE.test(html);
}

export interface RichTextContentProps {
  path: string;
  label?: string;
  description?: string;
  defaultField?: string;
  defaultValue?: string;
  as?: 'div' | 'span' | 'p' | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'li' | 'figcaption';
  className?: string;
  style?: React.CSSProperties;
}

export function RichTextContent({
  path,
  label,
  description,
  defaultField,
  defaultValue,
  as: Tag = 'div',
  className,
  style,
}: RichTextContentProps) {
  const { pagePaths } = useVibesites();
  const property = usePropertyWithBedroomOptions();
  const rawHtml = useContentToken(path, { defaultField, defaultValue });

  const resolvedHtml = useMemo(() => {
    if (!rawHtml) return '';
    const resolvedHtmlRaw = resolveRichText(rawHtml, property as Record<string, unknown>);
    const verifiedHtml = verifyCrossLinks(resolvedHtmlRaw, pagePaths);
    return collapseBlankParagraphs(stripWrappingParagraph(verifiedHtml, Tag));
  }, [rawHtml, property, pagePaths, Tag]);

  const effectiveTag =
    PHRASING_ONLY_TAGS.has(Tag) && hasBlockContent(resolvedHtml) ? 'div' : Tag;

  return (
    <EditableToken
      path={path}
      type="richtext"
      label={label}
      description={description}
      defaultField={defaultField}
      defaultValue={defaultValue}
      as={effectiveTag as 'div'}
      className={className}
      style={style}
    >
      <span
        data-vibesite-richtext=""
        dangerouslySetInnerHTML={{ __html: resolvedHtml }}
        style={{ display: 'contents' }}
      />
    </EditableToken>
  );
}

export default RichTextContent;
